import React, { useEffect, useMemo, useRef, useState } from 'react';
import { AddUniversal, TrashCanOutlined } from '@duxton-web/icons';
import {
  Button,
  Input,
  InputNumber,
  Radio,
  Space,
  Typography,
} from '@duxton-web/ui';

import { VariableType } from '@/constants';
import { useVariableContext } from '@/features/variable/contexts/variable-context';
import JsonSchema from '@/features/variable/settings/validation/json-schema';
import {
  getRangeMultipleOptionsForVariableType,
  RangeMultipleValidationType,
} from '@/features/variable/settings/validation/range-multiple-content';
import { ContextualRule } from '@/lib/PlannerDTOs';

import './index.css';

export interface ValidationContentProps {
  rule: ContextualRule;
  disabled?: boolean;
  onRuleChange: (rule: ContextualRule) => void;
}

const ValidationContent = ({
  rule,
  disabled,
  onRuleChange,
}: ValidationContentProps) => {
  const { variable } = useVariableContext();

  const isJsonRule = variable?.type === VariableType.JSON;
  const isStringRule = [VariableType.STRING, VariableType.FILE].includes(
    variable?.type as VariableType,
  );
  const rangeOptions = useMemo(
    () => getRangeMultipleOptionsForVariableType(variable?.type),
    [variable?.type],
  );

  const inferredValidationType =
    useMemo<RangeMultipleValidationType | null>(() => {
      if (Array.isArray(rule?.allowedValues) && rule.allowedValues.length > 0) {
        return RangeMultipleValidationType.MULTIPLE;
      }
      if (
        rule?.minimumLength != null ||
        rule?.maximumLength != null ||
        rule?.minimumValue != null ||
        rule?.maximumValue != null
      ) {
        return RangeMultipleValidationType.RANGE;
      }
      return null;
    }, [
      rule?.allowedValues,
      rule?.maximumLength,
      rule?.maximumValue,
      rule?.minimumLength,
      rule?.minimumValue,
    ]);

  const [validationType, setValidationType] =
    useState<RangeMultipleValidationType>(
      inferredValidationType ?? RangeMultipleValidationType.MULTIPLE,
    );

  const allowedValues = useMemo<Array<string | number | null>>(() => {
    if (isStringRule) {
      return ((rule?.allowedValues || []) as Array<string | number | null>).map(
        (v) => String(v ?? ''),
      );
    }
    return ((rule?.allowedValues || []) as Array<string | number | null>).map(
      (v) => (typeof v === 'number' ? v : null),
    );
  }, [isStringRule, rule?.allowedValues]);

  const getInvalidValueMessage = (value: string | number) => {
    const duplicates = allowedValues.filter((v) => v === value);
    const shouldValidateDuplicate = isStringRule ? !!value : value != null;
    if (shouldValidateDuplicate && duplicates.length > 1)
      return 'Duplicate value';
    return '';
  };

  const rangeMin = isStringRule ? rule?.minimumLength : rule?.minimumValue;
  const rangeMax = isStringRule ? rule?.maximumLength : rule?.maximumValue;
  const hasRangeOrderError =
    rangeMin != null && rangeMax != null && rangeMin >= rangeMax;
  const hasRangeEmptyError = rangeMin == null && rangeMax == null;

  const onRuleChangeRef = useRef(onRuleChange);
  onRuleChangeRef.current = onRuleChange;
  const ruleRef = useRef(rule);
  ruleRef.current = rule;

  useEffect(() => {
    if (disabled) return;
    if (validationType !== RangeMultipleValidationType.MULTIPLE) return;
    if (allowedValues.length > 0) return;

    onRuleChangeRef.current({
      ...ruleRef.current,
      allowedValues: (isStringRule ? [''] : [null]) as unknown as
        | string[]
        | number[],
    });
  }, [allowedValues.length, disabled, isStringRule, validationType]);

  useEffect(() => {
    if (inferredValidationType === null) return;

    const hasSelectedOption = rangeOptions.some(
      (o) => o.value === inferredValidationType,
    );
    if (!hasSelectedOption) {
      setValidationType(rangeOptions[0].value as RangeMultipleValidationType);
      return;
    }
    setValidationType(inferredValidationType);
  }, [inferredValidationType, rangeOptions]);

  const handleValidationTypeChange = (
    nextType: RangeMultipleValidationType,
  ) => {
    setValidationType(nextType);
    if (nextType === RangeMultipleValidationType.MULTIPLE) {
      onRuleChange({
        ...rule,
        minimumLength: undefined,
        maximumLength: undefined,
        minimumValue: undefined,
        maximumValue: undefined,
        allowedValues: isStringRule
          ? (allowedValues as string[])
          : (allowedValues as unknown as number[]),
      });
      return;
    }
    onRuleChange({ ...rule, allowedValues: undefined });
  };

  const handleAddAllowedValue = () => {
    onRuleChange({
      ...rule,
      allowedValues: isStringRule
        ? [...(allowedValues as string[]), '']
        : ([
            ...(allowedValues as Array<number | null>),
            null,
          ] as unknown as number[]),
    });
  };

  const handleAllowedValueUpdate = (
    index: number,
    nextValue: string | number | null,
  ) => {
    const nextAllowedValues = [...allowedValues];
    nextAllowedValues[index] = nextValue;
    onRuleChange({
      ...rule,
      allowedValues: isStringRule
        ? (nextAllowedValues as string[])
        : (nextAllowedValues as unknown as number[]),
    });
  };

  const handleAllowedValueDelete = (indexToDelete: number) => {
    const nextAllowedValues = allowedValues.filter(
      (_, i) => i !== indexToDelete,
    );
    onRuleChange({
      ...rule,
      allowedValues: isStringRule
        ? (nextAllowedValues as string[])
        : (nextAllowedValues as unknown as number[]),
    });
  };

  const handleRangeValueChange = (
    key: 'minimumLength' | 'maximumLength' | 'minimumValue' | 'maximumValue',
    value: number | null,
  ) => {
    onRuleChange({ ...rule, [key]: value == null ? undefined : value });
  };

  if (isJsonRule) {
    return (
      <JsonSchema
        value={rule.jsonSchema}
        onChange={(value, isValid) => {
          if (isValid) onRuleChange({ ...rule, jsonSchema: value });
        }}
      />
    );
  }

  return (
    <>
      <Radio.Group
        optionType="button"
        value={validationType}
        options={rangeOptions}
        disabled={disabled}
        onChange={(e) =>
          handleValidationTypeChange(
            e.target.value as RangeMultipleValidationType,
          )
        }
      />
      {validationType === RangeMultipleValidationType.MULTIPLE ? (
        <div
          id="enum-selections-container"
          style={{ display: 'flex', flexDirection: 'column', gap: 8 }}
        >
          <Typography.Metadata style={{ color: '#A3A3A3' }}>
            Valid Values
          </Typography.Metadata>
          <div
            style={{
              display: 'flex',
              alignItems: 'flex-start',
              flexWrap: 'wrap',
              gap: 8,
            }}
          >
            {allowedValues.map((value, index) =>
              isStringRule ? (
                <div
                  key={`allowed-value-${index}`}
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 8,
                  }}
                >
                  <Space.Compact className="validation-input">
                    <Input
                      disabled={disabled}
                      className="validation-input-number"
                      status={getInvalidValueMessage(value) ? 'error' : ''}
                      value={String(value ?? '')}
                      onChange={(e) =>
                        handleAllowedValueUpdate(index, e.target.value)
                      }
                    />
                    {allowedValues.length > 1 && (
                      <Button
                        className="enum-delete-button"
                        disabled={disabled}
                        danger={!!getInvalidValueMessage(value)}
                        shape="default"
                        icon={<TrashCanOutlined />}
                        onClick={() => handleAllowedValueDelete(index)}
                      />
                    )}
                  </Space.Compact>
                  {getInvalidValueMessage(value) && (
                    <Typography.Text type="danger">
                      {getInvalidValueMessage(value)}
                    </Typography.Text>
                  )}
                </div>
              ) : (
                <div
                  key={`allowed-value-${index}`}
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 8,
                  }}
                >
                  <Space.Compact>
                    <InputNumber
                      disabled={disabled}
                      className="validation-input-number"
                      status={getInvalidValueMessage(value) ? 'error' : ''}
                      value={typeof value === 'number' ? value : null}
                      onChange={(e) =>
                        handleAllowedValueUpdate(
                          index,
                          typeof e === 'number' ? e : null,
                        )
                      }
                    />
                    {allowedValues.length > 1 && (
                      <Button
                        className="enum-delete-button"
                        disabled={disabled}
                        danger={!!getInvalidValueMessage(value)}
                        shape="default"
                        icon={<TrashCanOutlined />}
                        onClick={() => handleAllowedValueDelete(index)}
                      />
                    )}
                  </Space.Compact>
                  {getInvalidValueMessage(value) && (
                    <Typography.Text type="danger">
                      {getInvalidValueMessage(value)}
                    </Typography.Text>
                  )}
                </div>
              ),
            )}
            {!disabled && (
              <Button
                style={{
                  width: '40px',
                  height: '40px',
                  borderRadius: '12px',
                  background: '#FFF',
                  border: '1px solid #E8E8E8',
                  padding: '0',
                }}
                icon={<AddUniversal />}
                onClick={handleAddAllowedValue}
              />
            )}
          </div>
        </div>
      ) : (
        <div
          id="validation-range-container"
          style={{ display: 'flex', flexDirection: 'column', gap: 8 }}
        >
          <Typography.Metadata style={{ color: '#A3A3A3' }}>
            {isStringRule ? 'Range of characters' : 'Range of values'}
          </Typography.Metadata>
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
            <InputNumber
              disabled={disabled}
              className="validation-prefix-input-number"
              status={hasRangeOrderError ? 'error' : ''}
              value={
                isStringRule
                  ? (rule?.minimumLength as number)
                  : (rule?.minimumValue as number)
              }
              prefix="MIN"
              style={{ width: 210 }}
              onChange={(e) =>
                handleRangeValueChange(
                  isStringRule ? 'minimumLength' : 'minimumValue',
                  typeof e === 'number' ? e : null,
                )
              }
            />
            <InputNumber
              disabled={disabled}
              className="validation-prefix-input-number"
              status={hasRangeOrderError ? 'error' : ''}
              value={
                isStringRule
                  ? (rule?.maximumLength as number)
                  : (rule?.maximumValue as number)
              }
              prefix="MAX"
              style={{ width: 210 }}
              onChange={(e) =>
                handleRangeValueChange(
                  isStringRule ? 'maximumLength' : 'maximumValue',
                  typeof e === 'number' ? e : null,
                )
              }
            />
          </div>
          {hasRangeOrderError && (
            <Typography.Text type="danger">
              Min cannot be greater or equal than Max
            </Typography.Text>
          )}
          {hasRangeEmptyError && (
            <Typography.Text type="danger">
              Min and/or Max is/are required
            </Typography.Text>
          )}
        </div>
      )}
    </>
  );
};

export default ValidationContent;
